import React from 'react';
import { format, parseISO } from 'date-fns';
import { Globe } from 'lucide-react';

import Link from '@dashboard/components/Link';
import { Skeleton } from '@dashboard/components/ui/skeleton';
import getPriceLabel from '@dashboard/services/translation/getPriceLabel';
import { StatChart } from '@dashboard/scenes/Dashboard/components/StatCard';

import * as KPICard from './KPICard';
import { usePageSummary } from './PageSummaryRange';

/** Money in the page's currency, for a trend of amounts. */
export function moneyFormatter(page) {
  // TODO(M14): reads legacy product scalars — repoint to prices[]/selected price before legacy fields are removed (product.currency)
  const currency = page.product?.currency ?? page.eventDetails?.currency;
  return amount => getPriceLabel({ amount, currency });
}

// The chart treats labels as categories, so two points must never share one: a range across
// years carries the year, and hours across days carry the day.
function pointFormat(unit, points) {
  const first = points[0]?.date ?? '';
  const last = points[points.length - 1]?.date ?? '';
  if (unit === 'hour') return first.slice(0, 10) === last.slice(0, 10) ? 'HH:mm' : 'd MMM HH:mm';
  if (unit === 'month') return 'MMM yyyy';
  if (unit === 'year') return 'yyyy';
  return first.slice(0, 4) === last.slice(0, 4) ? 'd MMM' : 'd MMM yyyy';
}

/** One metric over the Summary's date range, drawn small under a stat card's number. */
export function KPITrend(props) {
  const { summary, isLoading } = usePageSummary();
  return (
    <SeriesTrend points={summary?.series} unit={summary?.unit} isLoading={isLoading} {...props} />
  );
}

/** One metric of a series of { date, ...metrics } points, drawn small under a stat card's number. */
export function SeriesTrend({
  points = [],
  unit,
  isLoading,
  metric,
  label,
  formatValue = value => value,
}) {
  const values = points.map(point => point[metric] || 0);
  const labelFormat = pointFormat(unit, points);

  return (
    <div className="-mb-2 mt-1">
      <StatChart
        isLoading={isLoading}
        data={points.map((point, i) => ({
          timestamp: format(parseISO(point.date), labelFormat),
          [label]: values[i],
        }))}
        categories={[label]}
        maxValue={formatValue(Math.max(...values, 0))}
        minValue={formatValue(0)}
        valueFormatter={formatValue}
        heightClass="h-20"
      />
    </div>
  );
}

/** Where this page's views came from over the Summary's date range, as a stat card. */
export function TopReferrers() {
  const { summary, isLoading, isAllTime } = usePageSummary();

  return (
    <CountListCard
      icon={Globe}
      title="Top referrers"
      isLoading={isLoading}
      emptyText={isAllTime ? 'No referrers yet' : 'No referrers in this period'}
      rows={(summary?.referrers || []).map(ref => ({
        key: ref.referrer,
        count: ref.count,
        label: /^https?:\/\//.test(ref.referrer) ? (
          <Link to={ref.referrer} blank className="truncate font-normal">
            {ref.referrer.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}
          </Link>
        ) : (
          <span className="truncate">{ref.referrer}</span>
        ),
      }))}
    />
  );
}

/** A stat card listing counts with what they count, highest first, like Top referrers. */
export function CountListCard({ icon, title, info, rows, isLoading, emptyText }) {
  return (
    <KPICard.Container>
      <KPICard.Icon icon={icon} />
      <KPICard.Header>
        <KPICard.Title>{title}</KPICard.Title>
        {info && <KPICard.InfoTooltip>{info}</KPICard.InfoTooltip>}
      </KPICard.Header>
      {isLoading ? (
        <div className="mt-1 space-y-2">
          {[0, 1, 2].map(index => (
            <Skeleton key={index} className="h-4 w-full max-w-48" />
          ))}
        </div>
      ) : rows.length ? (
        <ul className="mt-1 space-y-1 text-sm" aria-label={title}>
          {rows.map(row => (
            <li key={row.key} className="flex min-w-0 items-baseline gap-3">
              <span className="w-8 shrink-0 font-medium tabular-nums text-gray-900 dark:text-foreground">
                {row.count}
              </span>
              {typeof row.label === 'string' ? (
                <span className="truncate">{row.label}</span>
              ) : (
                row.label
              )}
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-sm text-gray-500 dark:text-muted-foreground">{emptyText}</div>
      )}
    </KPICard.Container>
  );
}
